import { Box } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Vehicle } from '../types/vehicle';
import { ScheduledBatteryCheckList } from '../types/workshopSession';
import { KPI } from '../components/molecules/KPI';
import TableWidget, { TableColumn } from '../components/molecules/tableWidget';
import { TimeAgo } from '../components/timeAgo';
import { fetchBatteryChecks, fetchCheckedInVehicles } from '../api/vehicles';
import { Colors, Spacing } from '../constants/theme';
import Button from '../components/atoms/button';
import { Card } from '../components/atoms/card';
import { useAuth0 } from '@auth0/auth0-react';
import { useSelectedWorkshop } from '../context/workshopContext';

type VehicleTableRow = {
  id: string;
  vin: string;
  vehicleMake: string;
  vehicleModel: string;
  area: string;
  keyNumber: string;
  checkedInUtc: string;
  vehicle: Vehicle;
};

type BatteryCheckTableRow = {
  id: string;
  vehicleVin: string;
  keyNumber: string;
  dueLocal: string;
  checkedValue: number | string;
  batteryCheck: ScheduledBatteryCheckList;
};

export const Dashboard = () => {
  const [vehicles, setVehicles] = useState<Vehicle[]>()
  const [batteryChecksToday, setBatteryChecksToday] = useState<ScheduledBatteryCheckList[]>()
  const { selectedOrganisationId: workshopId } = useSelectedWorkshop()
  const navigate = useNavigate()
  const { isAuthenticated, isLoading } = useAuth0();

  useEffect(() => {
    const fetchVehicles = async () => {
      if (!workshopId) {
        return;
      }
      try {
        const data = await fetchCheckedInVehicles(workshopId)
        setVehicles(data)
      } catch (error) {
        console.error('error with fetching vehicles', error)
      }
    }
    if (isAuthenticated && !isLoading)
      fetchVehicles()
  }, [workshopId, isAuthenticated, isLoading])

  useEffect(() => {
    const fetchBatteryChecksToday = async () => {
      if (!workshopId) {
        return;
      }
      try {
        const data = await fetchBatteryChecks(workshopId, new Date().toISOString().split("T")[0])
        setBatteryChecksToday(data)
      } catch (error) {
        console.error('error with fetching battery checks', error)
      }
    }
    if (isAuthenticated && !isLoading)
      fetchBatteryChecksToday()
  }, [workshopId, isAuthenticated, isLoading])

  const vehicleTableData = useMemo<VehicleTableRow[]>(() =>
    [...(vehicles ?? [])]
      .sort((a, b) => new Date(b.checkedInUtc).getTime() - new Date(a.checkedInUtc).getTime())
      .slice(0, 5)
      .map((vehicle) => ({
        id: vehicle.vin,
        vin: vehicle.vin,
        vehicleMake: vehicle.vehicleMake,
        vehicleModel: vehicle.vehicleModel,
        area: vehicle.currentLocation ?? vehicle.area ?? '',
        keyNumber: vehicle.keyNumber ?? '',
        checkedInUtc: vehicle.checkedInUtc,
        vehicle,
      })),
    [vehicles])

  const vehicleColumns = useMemo<TableColumn[]>(() => [
    { id: 'vin', label: 'VIN' },
    { id: 'vehicleMake', label: 'Make' },
    { id: 'vehicleModel', label: 'Model' },
    { id: 'area', label: 'Location' },
    { id: 'keyNumber', label: 'Key number' },
    {
      id: 'checkedInUtc',
      label: 'Checked in',
      format: (value) => <TimeAgo date={String(value)} />,
    },
  ], [])

  const batteryCheckTableData = useMemo<BatteryCheckTableRow[]>(() =>
    (batteryChecksToday ?? [])
      .sort((a, b) => new Date(b.dueLocal).getTime() - new Date(a.dueLocal).getTime())
      .slice(0, 5)
      .map((batteryCheck) => ({
        id: batteryCheck.workSessionId,
        vehicleVin: batteryCheck.vehicleVin,
        keyNumber: batteryCheck.keyNumber ?? '',
        dueLocal: batteryCheck.dueLocal,
        checkedValue: batteryCheck.checkedValue ?? '',
        batteryCheck,
      })),
    [batteryChecksToday])

  const batteryCheckColumns = useMemo<TableColumn[]>(() => [
    { id: 'vehicleVin', label: 'VIN' },
    { id: 'keyNumber', label: 'Key number' },
    {
      id: 'dueLocal',
      label: 'Due',
      format: (value) => <TimeAgo date={String(value)} />,
    },
    {
      id: 'checkedValue',
      label: 'Last check',
      format: (value) => value || value === 0 ? `${value} V` : '—',
    },
  ], [])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', padding: Spacing.padding*2}}>
      <Box sx={{ display: 'flex', flexDirection: 'row', paddingTop: Spacing.padding, gap: Spacing.gap *2}}>
        <Card extend={{ flex: '1' }}> 
          <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-end' }}>
            <KPI value={vehicles?.length || 0} header="Total vehicles" description="checked in" />
            <Button onClick={() => navigate('/manage/manage_vehicles')} extend={{ color: Colors.light.green }}>View all</Button>
          </Box>
          <TableWidget
            data={vehicleTableData}
            columns={vehicleColumns}
            defaultSort="checkedInUtcdesc"
            onClickRow={(_event, _id, row) =>
              navigate(`/vehicle/${(row as VehicleTableRow).vehicle.vin}/${(row as VehicleTableRow).vehicle.workshopSessionId}`)
            }
            themes={{ device: 'desktop' }}
            pagination={false}
          />
        </Card>
        <Card extend={{ flex: '1' }}>
          <KPI value={batteryChecksToday?.length || 0} header="Battery checks" description="due today or overdue" />
          <TableWidget
            data={batteryCheckTableData}
            columns={batteryCheckColumns}
            defaultSort="dueLocaldesc"
            onClickRow={(_event, _id, row) =>
              navigate(`/vehicle/${(row as BatteryCheckTableRow).batteryCheck.vehicleVin}/${(row as BatteryCheckTableRow).batteryCheck.workSessionId}`)
            }
            themes={{ device: 'desktop' }}
            pagination={false}
          />
        </Card>
      </Box>
    </Box>
  );
};
